import { Injectable, OnDestroy, signal, computed } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { JobProgressEvent, JobCompletedEvent, JobErrorEvent, SubscribedEvent, UnsubscribedEvent } from '../types';

/**
 * Socket.IO client for real-time job progress tracking
 *
 * Connects to the `/jobs` namespace and manages subscriptions
 * to individual jobs via room-based messaging.
 *
 * @example
 * ```typescript
 * const gateway = inject(JobsGatewayClient);
 *
 * // Subscribe to a job
 * gateway.subscribe('ai-scenarios', 'job-123');
 *
 * // Get progress signal
 * const progress = gateway.getJobProgress('ai-scenarios', 'job-123');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class JobsGatewayClient implements OnDestroy {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;

  // Signal-based state
  private readonly _connected = signal(false);
  private readonly _jobProgress = signal<Record<string, JobProgressEvent>>({});
  private readonly _jobCompleted = signal<Record<string, JobCompletedEvent>>({});
  private readonly _jobErrors = signal<Record<string, JobErrorEvent>>({});
  private readonly _subscriptions = signal<Set<string>>(new Set());

  // Public readonly signals
  readonly connected = this._connected.asReadonly();
  readonly jobProgress = this._jobProgress.asReadonly();
  readonly jobCompleted = this._jobCompleted.asReadonly();
  readonly jobErrors = this._jobErrors.asReadonly();

  constructor() {
    this.connect();
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Connect to the jobs WebSocket namespace
   */
  connect(): void {
    if (this.socket) {
      return;
    }

    this.socket = io('/jobs', {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.MAX_RECONNECT_ATTEMPTS,
      transports: ['websocket', 'polling'],
    });

    this.setupEventListeners();
  }

  /**
   * Disconnect from the WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this._connected.set(false);
  }

  reset(): void {
    this.disconnect();
    this._jobProgress.set({});
    this._jobCompleted.set({});
    this._jobErrors.set({});
    this._subscriptions.set(new Set());
    this.reconnectAttempts = 0;
  }

  reconnect(): void {
    this.reset();
    this.connect();
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this._connected.set(true);
      this.reconnectAttempts = 0;

      // Re-subscribe to all previously subscribed jobs
      const subscriptions = this._subscriptions();
      for (const key of subscriptions) {
        const [queueName, jobId] = key.split(':');
        this.socket?.emit('subscribe', { queueName, jobId });
      }
    });

    this.socket.on('disconnect', () => {
      this._connected.set(false);
    });

    this.socket.on('connect_error', () => {
      this.reconnectAttempts++;
    });

    this.socket.on('progress', (data: JobProgressEvent) => {
      const key = `${data.queueName}:${data.jobId}`;
      this._jobProgress.update(prev => ({ ...prev, [key]: data }));
    });

    this.socket.on('completed', (data: JobCompletedEvent) => {
      const key = `${data.queueName}:${data.jobId}`;
      this._jobCompleted.update(prev => ({ ...prev, [key]: data }));
      // Clear progress on completion
      this._jobProgress.update(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    });

    this.socket.on('error', (data: JobErrorEvent) => {
      const key = `${data.queueName}:${data.jobId}`;
      this._jobErrors.update(prev => ({ ...prev, [key]: data }));
      // Clear progress on error
      this._jobProgress.update(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    });

    this.socket.on('subscribed', (data: SubscribedEvent) => {
      const key = `${data.queueName}:${data.jobId}`;
      this._subscriptions.update(prev => new Set(prev).add(key));
    });

    this.socket.on('unsubscribed', (data: UnsubscribedEvent) => {
      const key = `${data.queueName}:${data.jobId}`;
      this._subscriptions.update(prev => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    });
  }

  /**
   * Subscribe to progress updates for a specific job
   */
  subscribe(queueName: string, jobId: string): void {
    const key = `${queueName}:${jobId}`;
    this._subscriptions.update(prev => new Set(prev).add(key));

    if (this.socket?.connected) {
      this.socket.emit('subscribe', { queueName, jobId });
    }
  }

  /**
   * Unsubscribe from progress updates for a specific job
   */
  unsubscribe(queueName: string, jobId: string): void {
    const key = `${queueName}:${jobId}`;
    this._subscriptions.update(prev => {
      const copy = new Set(prev);
      copy.delete(key);
      return copy;
    });

    if (this.socket?.connected) {
      this.socket.emit('unsubscribe', { queueName, jobId });
    }

    // Clear state for this job
    this._jobProgress.update(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    this._jobCompleted.update(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    this._jobErrors.update(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  }

  /**
   * Get progress for a specific job as a computed signal
   */
  getJobProgress(queueName: string, jobId: string) {
    const key = `${queueName}:${jobId}`;
    return computed(() => this._jobProgress()[key]?.progress);
  }

  /**
   * Get the full progress event (percent + optional `message` phase label) for a
   * specific job as a computed signal. Use when the UI needs the phase name, not
   * just the percentage.
   */
  getJobProgressEvent(queueName: string, jobId: string) {
    const key = `${queueName}:${jobId}`;
    return computed(() => this._jobProgress()[key]);
  }

  /**
   * Get completed result for a specific job as a computed signal
   */
  getJobCompleted<T>(queueName: string, jobId: string) {
    const key = `${queueName}:${jobId}`;
    return computed(() => this._jobCompleted()[key] as JobCompletedEvent<T> | undefined);
  }

  /**
   * Get error for a specific job as a computed signal
   */
  getJobError(queueName: string, jobId: string) {
    const key = `${queueName}:${jobId}`;
    return computed(() => this._jobErrors()[key]?.error);
  }

  /**
   * Check if subscribed to a specific job
   */
  isSubscribed(queueName: string, jobId: string): boolean {
    const key = `${queueName}:${jobId}`;
    return this._subscriptions().has(key);
  }
}
